import { CanvasType, LockSettingFields, LockSettingTypeList } from "../../../constants/schema.js";
import { zoomLevelInRange } from "../../../utils/validation.js";
import { Tag, attachActionsTag } from "../../tag.js";
import { bHas } from "../../../utils/bit.js";
import { viewChangeZoomLevelAction, viewScrollToAction, viewStreamScrollToAction, viewStreamZoomLevelAction } from "../editor/view.actions.js";
import { getZoomTransform, toScenePoint, toScreenPoint } from "../../../konva/scene/viewport.js";
import { UnlockCarriedSettings, changeBracketTypeAction, changeColumnNameCaseAction, changeDatabaseAction, changeLanguageAction, changeLockSettingsAction, changeShowAction, changeTableNameCaseAction, changeZoomLevelAction, scrollToAction, streamScrollToAction, streamZoomLevelAction } from "./atom.actions.js";
import { endScreenSettingsAction } from "../editor/atom.actions.js";
import { isNil, pick } from "es-toolkit";
import { round } from "es-toolkit/compat";
//#region src/engine/modules/settings/generator.actions.ts
function getMovementToPlace(transform, nextZoomLevel, scene, screen) {
	const landed = toScreenPoint({
		...transform,
		zoomLevel: nextZoomLevel
	}, scene);
	return {
		movementX: round(screen.x - landed.x, 4),
		movementY: round(screen.y - landed.y, 4)
	};
}
function getMovementScrollTo(state, transform, nextZoomLevel) {
	const { viewport } = state.editor;
	const center = {
		x: viewport.width / 2,
		y: viewport.height / 2
	};
	return getMovementToPlace(transform, nextZoomLevel, toScenePoint(transform, center), center);
}
var changeZoomLevelAction$ = (value, source) => function* (state) {
	const transform = getZoomTransform(state, source);
	const { originX, originY } = transform;
	const { movementX, movementY } = getMovementScrollTo(state, transform, zoomLevelInRange(value));
	const origin = {
		originX: originX + movementX,
		originY: originY + movementY
	};
	if (source && source !== "document") {
		yield viewChangeZoomLevelAction({
			value,
			kind: source
		});
		yield viewScrollToAction({
			...origin,
			kind: source
		});
		return;
	}
	yield changeZoomLevelAction({ value });
	yield scrollToAction(origin);
};
var streamZoomLevelAction$ = (value, source) => function* (state) {
	const transform = getZoomTransform(state, source);
	const movement = getMovementScrollTo(state, transform, zoomLevelInRange(transform.zoomLevel + value));
	if (source && source !== "document") {
		yield viewStreamZoomLevelAction({
			value,
			kind: source
		});
		yield viewStreamScrollToAction({
			...movement,
			kind: source
		});
		return;
	}
	yield streamZoomLevelAction({ value });
	yield streamScrollToAction(movement);
};
var pinchZoomAction$ = ({ zoomLevel, screen, scene }, source) => function* (state) {
	const transform = getZoomTransform(state, source);
	const nextZoomLevel = zoomLevelInRange(zoomLevel);
	const value = round(nextZoomLevel - transform.zoomLevel, 2);
	const movement = getMovementToPlace(transform, nextZoomLevel, scene ?? toScenePoint(transform, screen), screen);
	const kind = source && source !== "document" ? source : null;
	if (value !== 0) yield kind ? viewStreamZoomLevelAction({
		value,
		kind
	}) : streamZoomLevelAction({ value });
	if (movement.movementX !== 0 || movement.movementY !== 0) yield kind ? viewStreamScrollToAction({
		...movement,
		kind
	}) : streamScrollToAction(movement);
};
var changeLockSettingsAction$ = (lockSettingType, value) => function* ({ settings, editor }) {
	const named = value ? lockSettingType : lockSettingType & UnlockCarriedSettings;
	const fields = LockSettingTypeList.filter((bit) => bHas(named, bit)).flatMap((bit) => LockSettingFields[bit]);
	const values = pick(settings, fields);
	if (values.canvasType === CanvasType.settings) values.canvasType = editor.lastCanvasType;
	yield changeLockSettingsAction({
		lockSettingType,
		value,
		values
	});
};
var RESTORED_SETTERS = {
	language: changeLanguageAction,
	tableNameCase: changeTableNameCaseAction,
	columnNameCase: changeColumnNameCaseAction,
	bracketType: changeBracketTypeAction,
	database: changeDatabaseAction
};
var RESTORED_UNITS = [...Object.keys(RESTORED_SETTERS), "show"];
function screenSettingRestores(values, settings) {
	const restores = [];
	Object.entries(RESTORED_SETTERS).forEach(([unit, setter]) => {
		const value = values[unit];
		if (!isNil(value) && value !== settings[unit]) restores.push(setter({ value }));
	});
	const { show } = values;
	if (!isNil(show)) {
		const set = show & ~settings.show;
		const clear = settings.show & ~show;
		if (set) restores.push(changeShowAction({
			show: set,
			value: true
		}));
		if (clear) restores.push(changeShowAction({
			show: clear,
			value: false
		}));
	}
	return restores;
}
var restoreScreenSettingsAction$ = () => function* ({ editor: { screenBaseline }, settings }) {
	const units = RESTORED_UNITS.filter((unit) => !isNil(screenBaseline?.[unit]));
	if (!screenBaseline || !units.length) return;
	yield [...attachActionsTag(Tag.screen, screenSettingRestores(screenBaseline, settings)), endScreenSettingsAction({ units })];
};
var actions$ = {
	changeZoomLevelAction$,
	streamZoomLevelAction$,
	changeLockSettingsAction$
};
//#endregion
export { actions$, changeLockSettingsAction$, changeZoomLevelAction$, pinchZoomAction$, restoreScreenSettingsAction$, screenSettingRestores, streamZoomLevelAction$ };
